import { currencyDigits } from "@/shared/currency-digits";

/** `format` takes an exact decimal string since ES2023; the ES2022 lib types only numbers. */
const exact = (value: string) => value as unknown as number;

/**
 * Integer minor units to the printed amount. The bigint becomes an exact decimal string and
 * `Intl.NumberFormat` formats the string, so a total never passes through a float. Lakh grouping
 * belongs to INR alone.
 */
export function formatMoney(
  minor: bigint,
  currency: string,
  { dropZeroFraction = false } = {},
): string {
  const digits = minorDigits(currency);
  // `trailingZeroDisplay` is ES2023 too; a price prints $8 but keeps $8.50.
  const options: Intl.NumberFormatOptions & { trailingZeroDisplay?: string } = {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    trailingZeroDisplay: dropZeroFraction ? "stripIfInteger" : "auto",
  };
  const format = new Intl.NumberFormat(
    currency === "INR" ? "en-IN" : "en",
    options,
  );
  // A discount prints with a minus sign; `Intl` gives a hyphen.
  return format.format(exact(decimal(minor, digits))).replace("-", "\u2212");
}

/** A currency a salary or an invoice can be paid in. */
export const isTenderCurrency = (code: string) => currencyDigits(code) !== null;

/**
 * How many minor digits a currency has: 2 for INR, 0 for JPY. From ISO 4217, not `Intl`, whose
 * default differs by engine. XXX is a draft's before it has a currency, and counts in hundredths.
 */
export function minorDigits(currency: string): number {
  const digits = currencyDigits(currency) ?? (currency === "XXX" ? 2 : null);
  if (digits === null)
    throw new Error(`${currency} has no ISO 4217 minor digits`);
  return digits;
}

/** `118050n` with two digits is `"1180.50"`. NPCI's `am` takes the same form. */
export function decimal(minor: bigint, digits: number): string {
  const sign = minor < 0n ? "-" : "";
  const units = (minor < 0n ? -minor : minor)
    .toString()
    .padStart(digits + 1, "0");
  const whole = units.slice(0, units.length - digits);
  const fraction = units.slice(units.length - digits);
  return `${sign}${whole}${digits ? `.${fraction}` : ""}`;
}

/**
 * A typed amount to minor units, the inverse of `decimal`: "₹1,500", "Rs 1500" or "1500.5" with
 * two digits is `150050n`. Reads back the prefix `Intl` prints for any currency: "AED", "CA$",
 * "F CFA", "Cg.". Null for anything that is not a plain positive amount.
 */
export function minorOf(typed: string, digits: number): bigint | null {
  const match = /^(\d{1,13})(?:\.(\d*))?$/.exec(
    typed
      // Direction marks Intl puts around a symbol, and the Saudi riyal sign (U+20C1, Unicode 17),
      // which engines on older Unicode tables do not class as \p{Sc}.
      .replaceAll(/[\u200e\u200f\u061c]/gu, "")
      .replace(
        /^\s*(?:[a-z]{0,3}[\p{Sc}\u20c1]|f\s?cfa|cg\.|[a-z]{3,4}|rs\.?)/iu,
        "",
      )
      .replaceAll(/[,\s]/g, ""),
  );
  const fraction = match?.[2] ?? "";
  if (!match?.[1] || fraction.length > digits) return null;
  return BigInt(match[1] + fraction.padEnd(digits, "0"));
}
